<script setup lang="ts">
import ShipmentStatusBadge from '@/components/shipment/shared/ShipmentStatusBadge.vue';
import DataTable from 'primevue/datatable';
import type {
  DataTablePageEvent,
  DataTableSortEvent,
} from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import { RouterLink, useRouter } from 'vue-router';
import { computed, ref } from 'vue';
import { formatTime } from '@/filters';
import { displayStatusForRow, type ShipmentListRow } from '@/types/shipments';
import { useShipmentListActions } from '@/composables/useShipmentListActions';
import type { V2ShipmentDetail } from '@gpx-spa/queries/src/getAccountShipmentV2';
import ShipmentExceptionFlagsIcons from './ShipmentExceptionFlagsIcons.vue';
import NamedTrackerSet from '@/components/shipment/shared/NamedTrackerSet.vue';
import { shipmentEndpointDisplayName } from '@/utils/shipment-endpoint-display';
import { namedTrackersFromShipment } from '@/utils/named-tracker-set';

const props = defineProps<{
  isLoading: boolean;
  shipments: ShipmentListRow[];
  totalRecords: number;
  accountHasNoShipments: boolean;
  currentPage: number;
  pageSize: number;
  sortField: string | null;
  sortOrder: number;
  selectedShipments: ShipmentListRow[];
  canManage: boolean;
}>();

const emit = defineEmits<{
  page: [event: DataTablePageEvent];
  sort: [event: DataTableSortEvent];
  'clear-filters': [];
  'update:selectedShipments': [value: ShipmentListRow[]];
}>();

const router = useRouter();

const {
  editShipment,
  confirmClose,
  confirmDelete,
  canCloseShipment,
  canEditShipment,
  canDeleteShipment,
} = useShipmentListActions({
  onSelectionCleared: () => emit('update:selectedShipments', []),
});

const selection = computed({
  get: () => props.selectedShipments,
  set: (value) => emit('update:selectedShipments', value ?? []),
});

const rowMenuRef = ref<InstanceType<typeof Menu> | null>(null);
const rowMenuItems = ref<MenuItem[]>([]);

function formatDate(date: string | null): string {
  if (!date) return '-';
  return formatTime(date, 'M/d h:mm aa');
}

function resolveAddress(
  shipment: ShipmentListRow,
  direction: 'from' | 'to',
): string {
  return shipmentEndpointDisplayName(shipment as V2ShipmentDetail, direction);
}

function namedTrackersForShipment(shipment: ShipmentListRow) {
  return namedTrackersFromShipment(shipment as V2ShipmentDetail);
}

function onRowClick(e: { originalEvent?: Event; data: ShipmentListRow }) {
  const target = e.originalEvent?.target as HTMLElement | undefined;
  if (
    target?.closest(
      '.p-checkbox, .p-datatable-column-title, button, a, .p-menu, [data-row-actions]',
    )
  ) {
    return;
  }
  router.push({ name: 'ShipmentDetailsView', params: { id: e.data.id } });
}

function openRowMenu(event: Event, shipment: ShipmentListRow) {
  const items: MenuItem[] = [];
  if (canEditShipment(shipment)) {
    items.push({
      label: 'Edit',
      icon: 'pi pi-pencil',
      command: () => editShipment(shipment as V2ShipmentDetail),
    });
  }
  if (canCloseShipment(shipment)) {
    items.push({
      label: 'Closed',
      icon: 'pi pi-check-circle',
      command: () => confirmClose([shipment.id]),
    });
  }
  if (canDeleteShipment()) {
    items.push({
      label: 'Delete',
      icon: 'pi pi-trash',
      class: 'text-red-600',
      command: () => confirmDelete([shipment.id]),
    });
  }
  if (!items.length) {
    return;
  }
  rowMenuItems.value = items;
  rowMenuRef.value?.toggle(event);
}
</script>

<template>
  <div>
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-24 text-sm text-gray-400"
    >
      Loading…
    </div>

    <div
      v-else-if="shipments.length === 0"
      class="flex items-center justify-center"
    >
      <div class="py-12 text-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1"
          stroke="currentColor"
          class="mx-auto mb-4 size-12 text-gray-300"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0-3-3m3 3 3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z"
          />
        </svg>
        <template v-if="accountHasNoShipments">
          <h3 class="mb-1 text-base font-semibold text-gray-900">
            No shipments yet
          </h3>
          <p
            v-if="canManage"
            class="mx-auto mb-5 max-w-sm text-sm text-gray-500"
          >
            Create your first shipment to get started.
          </p>
          <RouterLink v-if="canManage" :to="{ name: 'ShipmentCreateView' }">
            <Button
              label="Create Shipment"
              size="small"
              class="!border-gpx_terracotta-100 !bg-gpx_terracotta-100 hover:!bg-gpx_terracotta-200"
            />
          </RouterLink>
        </template>
        <template v-else>
          <h3 class="mb-1 text-base font-semibold text-gray-900">
            No matching shipments
          </h3>
          <p class="mx-auto mb-5 max-w-sm text-sm text-gray-500">
            Try adjusting your filters.
          </p>
          <button
            type="button"
            class="text-gpx_red text-sm font-medium hover:underline"
            @click="emit('clear-filters')"
          >
            Clear filters
          </button>
        </template>
      </div>
    </div>

    <div
      v-else
      class="overflow-hidden rounded-lg border border-gray-200 bg-white"
    >
      <DataTable
        v-model:selection="selection"
        :value="shipments"
        :lazy="true"
        :paginator="true"
        :rows="pageSize"
        :totalRecords="totalRecords"
        :first="(currentPage - 1) * pageSize"
        :sortField="sortField"
        :sortOrder="sortOrder"
        dataKey="id"
        scrollable
        size="small"
        :rowClass="() => 'cursor-pointer'"
        @page="emit('page', $event)"
        @sort="emit('sort', $event)"
        @row-click="onRowClick"
      >
        <Column
          v-if="canManage"
          selectionMode="multiple"
          headerStyle="width: 3rem"
        />
        <Column field="status" header="Status" style="min-width: 110px">
          <template #body="{ data }">
            <ShipmentStatusBadge :status="displayStatusForRow(data)" />
          </template>
        </Column>
        <Column
          field="name"
          header="Reference ID"
          sortable
          style="min-width: 160px"
        >
          <template #body="{ data }">
            <span class="text-sm font-medium text-gray-900">{{
              data.name || '-'
            }}</span>
          </template>
        </Column>
        <Column header="Route" style="min-width: 220px">
          <template #body="{ data }">
            <div class="flex max-w-[220px] flex-col gap-0.5">
              <div class="flex items-center gap-1.5 text-sm text-gray-900">
                <span class="size-1.5 shrink-0 rounded-full bg-green-500" />
                <span class="truncate">{{ resolveAddress(data, 'from') }}</span>
              </div>
              <div class="flex items-center gap-1.5 text-sm text-gray-500">
                <span class="size-1.5 shrink-0 rounded-full bg-slate-700" />
                <span class="truncate">{{ resolveAddress(data, 'to') }}</span>
              </div>
            </div>
          </template>
        </Column>
        <Column header="Device" style="min-width: 130px">
          <template #body="{ data }">
            <NamedTrackerSet :trackers="namedTrackersForShipment(data)" />
          </template>
        </Column>
        <Column
          field="carrier"
          header="Carrier"
          sortable
          style="min-width: 120px"
        >
          <template #body="{ data }">
            <span class="text-sm text-gray-600">{{ data.carrier || '—' }}</span>
          </template>
        </Column>
        <Column
          header="Planned Pickup"
          sortable
          field="start_at"
          style="min-width: 150px"
        >
          <template #body="{ data }">
            <span class="text-sm text-gray-900">{{
              formatDate(data.start_at as string | null)
            }}</span>
          </template>
        </Column>
        <Column
          header="Planned Delivery"
          sortable
          field="end_at"
          style="min-width: 150px"
        >
          <template #body="{ data }">
            <span class="text-sm text-gray-900">{{
              formatDate(data.end_at as string | null)
            }}</span>
          </template>
        </Column>
        <Column header="Exceptions" style="min-width: 6rem">
          <template #body="{ data }">
            <ShipmentExceptionFlagsIcons
              :flags="(data as ShipmentListRow).exception_flags"
            />
          </template>
        </Column>
        <Column header="" style="width: 7rem" class="shrink-0">
          <template #body="{ data }">
            <div class="flex items-center justify-end gap-1">
              <Button
                v-if="canEditShipment(data)"
                type="button"
                label="Edit"
                size="small"
                severity="secondary"
                outlined
                class="!px-2.5 !py-1 !text-xs"
                @click.stop="editShipment(data as V2ShipmentDetail)"
              />
              <div
                v-if="canManage"
                data-row-actions
                class="inline-flex"
                @click.stop
              >
                <Button
                  icon="pi pi-ellipsis-v"
                  text
                  rounded
                  severity="secondary"
                  size="small"
                  aria-label="Shipment actions"
                  @click="openRowMenu($event, data)"
                />
              </div>
            </div>
          </template>
        </Column>
      </DataTable>
      <Menu ref="rowMenuRef" :model="rowMenuItems" popup />
    </div>
  </div>
</template>
